/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, useReducer } from 'react';
import { SignedDocument, SseResultObject } from '../states';
import {
  initialStateOperationStarted,
  initialStateIdCaptured,
  initialStateSelfieCaptured,
  initialStateFacialAuthentication,
  initialStateFacialExtraction,
  initialStatePassiveLiveness,
  initialStateIdValiated,
  initialStateCivilRegistry,
  initialStatePreviousOnboarding,
  initialStateOperationFinished,
  initialStateOperationExpired,
  initialStateDocumentSignature,
  initialStatePersonalInformationGathered,
} from './InitialReducersStates';
import {
  operationStartedReducer,
  idCapturedReducer,
  selfieCapturedReducer,
  facialAuthenticationReducer,
  passiveLivenessReducer,
  idValidatedReducer,
  civilRegistryReducer,
  previousOnboardingReducer,
  facialExtractionReducer,
  operationFinishedReducer,
  operationExpiredReducer,
  documentSignatureReducer,
  personalInformationReducer,
} from './Reducers';
import { ActionTypeRequest, StateRequest } from './RequestReducer';

export enum DispatchersTitles {
  ID_CAPTURED = 'IdCaptured',
  SELFIE_CAPTURED = 'SelfieCaptured',
  FACIAL_AUTHENTICATION_EVALUATED = 'FacialAuthenticationEvaluated',
  PASSIVE_LIVENESS_EVALUATED = 'PassiveLivenessEvaluated',
  ID_VALIDATED = 'IdValidated',
  CIVIL_REGISTRY_VALIDATED = 'CivilRegistryValidated',
  PREVIOUS_ONBOARDING_VALIDATED = 'PreviousOnboardingValidated',
  OPERATION_FINISHED = 'OperationFinished',
  OPERATION_STARTED = 'OperationStarted',
  FACIAL_EXTRACTION_EVALUATED = 'FacialExtractionEvaluated',
  OPERATION_EXPIRED = 'OperationExpired',
  DOCUMENT_SIGNATURE = 'DocumentSignature',
  PERSONAL_INFORMATION_GATHERED = 'PersonalInformationGathered',
}

export type Dispatchers = {
  [K in DispatchersTitles as `dispatch${K}`]: Dispatch<ActionTypeRequest<any>>;
};

export type CardStates = {
  operationStarted: StateRequest<SseResultObject['OPERATION_STARTED']>;
  idCaptured: StateRequest<SseResultObject['ID_CAPTURED']>;
  selfieCaptured: StateRequest<SseResultObject['SELFIE_CAPTURED']>;
  facialAuthenticationEvaluated: StateRequest<
    SseResultObject['FACIAL_AUTHENTICATION_EVALUATED']
  >;
  facialExtractionEvaluated: StateRequest<
    SseResultObject['FACIAL_EXTRACTION_EVALUATED']
  >;
  passiveLivenessEvaluated: StateRequest<
    SseResultObject['PASSIVE_LIVENESS_EVALUATED']
  >;
  idValidated: StateRequest<SseResultObject['ID_VALIDATED']>;
  civilRegistryValidated: StateRequest<
    SseResultObject['CIVIL_REGISTRY_VALIDATED']
  >;
  previousOnboardingValidated: StateRequest<
    SseResultObject['PREVIOUS_ONBOARDING_VALIDATED']
  >;
  operationFinished: StateRequest<SseResultObject['OPERATION_FINISHED']>;
  operationExpired: StateRequest<SseResultObject['OPERATION_EXPIRED']>;
  documentSignature: StateRequest<SignedDocument>;
  personalInformationGathered: StateRequest<
    SseResultObject['PERSONAL_INFORMATION_GATHERED']
  >;
};

export const useDispatchers = () => {
  const [operationStarted, dispatchOperationStarted] = useReducer(
    operationStartedReducer,
    initialStateOperationStarted
  );

  const [idCaptured, dispatchIdCaptured] = useReducer(
    idCapturedReducer,
    initialStateIdCaptured
  );

  const [selfieCaptured, dispatchSelfieCaptured] = useReducer(
    selfieCapturedReducer,
    initialStateSelfieCaptured
  );

  const [facialAuthenticationEvaluated, dispatchFacialAuthenticationEvaluated] =
    useReducer(facialAuthenticationReducer, initialStateFacialAuthentication);

  const [facialExtractionEvaluated, dispatchFacialExtractionEvaluated] =
    useReducer(facialExtractionReducer, initialStateFacialExtraction);

  const [passiveLivenessEvaluated, dispatchPassiveLivenessEvaluated] =
    useReducer(passiveLivenessReducer, initialStatePassiveLiveness);

  const [idValidated, dispatchIdValidated] = useReducer(
    idValidatedReducer,
    initialStateIdValiated
  );

  const [civilRegistryValidated, dispatchCivilRegistryValidated] = useReducer(
    civilRegistryReducer,
    initialStateCivilRegistry
  );

  const [previousOnboardingValidated, dispatchPreviousOnboardingValidated] =
    useReducer(previousOnboardingReducer, initialStatePreviousOnboarding);

  const [operationFinished, dispatchOperationFinished] = useReducer(
    operationFinishedReducer,
    initialStateOperationFinished
  );

  const [operationExpired, dispatchOperationExpired] = useReducer(
    operationExpiredReducer,
    initialStateOperationExpired
  );

  const [documentSignature, dispatchDocumentSignature] = useReducer(
    documentSignatureReducer,
    initialStateDocumentSignature
  );

  const [personalInformationGathered, dispatchPersonalInformationGathered] =
    useReducer(
      personalInformationReducer,
      initialStatePersonalInformationGathered
    );

  const dispatchers: Dispatchers = {
    dispatchOperationStarted,
    dispatchIdCaptured,
    dispatchSelfieCaptured,
    dispatchFacialAuthenticationEvaluated,
    dispatchFacialExtractionEvaluated,
    dispatchPassiveLivenessEvaluated,
    dispatchIdValidated,
    dispatchCivilRegistryValidated,
    dispatchPreviousOnboardingValidated,
    dispatchOperationFinished,
    dispatchOperationExpired,
    dispatchDocumentSignature,
    dispatchPersonalInformationGathered,
  };

  const cardStates: CardStates = {
    operationStarted,
    idCaptured,
    selfieCaptured,
    facialAuthenticationEvaluated,
    facialExtractionEvaluated,
    passiveLivenessEvaluated,
    idValidated,
    civilRegistryValidated,
    previousOnboardingValidated,
    operationFinished,
    operationExpired,
    documentSignature,
    personalInformationGathered,
  };

  return {
    dispatchers,
    cardStates,
  };
};
